export const V2_METRICS = [
  { key: 'cost', label: 'Cost', format: 'currency' },
  { key: 'impressions', label: 'Impressions', format: 'int' },
  { key: 'clicks', label: 'Clicks', format: 'int' },
  { key: 'ctr', label: 'CTR', format: 'pct' },
  { key: 'cpc', label: 'Avg. CPC', format: 'currency2' },
  { key: 'cpm', label: 'CPM', format: 'currency2' },
  { key: 'conversions', label: 'Conversions', format: 'ratio' },
  { key: 'conversionsValue', label: 'Conversion value', format: 'currency2' },
  { key: 'cpa', label: 'Cost / conversion', format: 'currency2' },
  { key: 'roas', label: 'ROAS', format: 'ratio' },
  { key: 'valuePerConversion', label: 'Value / conversion', format: 'currency2' },
  { key: 'allConversions', label: 'All conversions', format: 'ratio' },
  { key: 'allConversionsValue', label: 'All conversion value', format: 'currency2' },
  { key: 'viewThroughConversions', label: 'View-through conversions', format: 'ratio' },
  { key: 'interactions', label: 'Interactions', format: 'int' },
  { key: 'interactionRate', label: 'Interaction rate', format: 'pct' },
  { key: 'conversionRate', label: 'Conversion rate', format: 'pct' },
];
const additive = ['cost', 'impressions', 'clicks', 'conversions', 'conversionsValue', 'allConversions', 'allConversionsValue', 'viewThroughConversions', 'interactions'];
const ratio = (a, b) => a == null || !b ? null : a / b;
// Missing reports do not invalidate the currency of campaigns that do report.
// A monetary contributor with an unknown currency still prevents aggregation.
export function reportingCurrency(campaigns) {
  const contributors = campaigns.filter(c => [c.performanceV2?.metrics, ...(c.performanceV2?.daily || [])].some(m =>
    ['cost', 'conversionsValue'].some(key => m?.[key] != null && Number.isFinite(Number(m[key])))));
  const currencies = [...new Set(contributors.map(c => c.performanceV2?.currency || c.currency_code || null))];
  const currency = currencies.length === 1 && /^[A-Z]{3}$/.test(currencies[0] || '') ? currencies[0] : null;
  return { currency, mixed: !currency };
}
export function aggregateMetrics(rows, mixedCurrency = false) {
  const out = {};
  for (const key of additive) out[key] = rows.length && rows.every((r) => r[key] != null && Number.isFinite(Number(r[key])))
    ? rows.reduce((sum, r) => sum + Number(r[key]), 0) : null;
  if (mixedCurrency) for (const key of ['cost', 'conversionsValue', 'allConversionsValue']) out[key] = null;
  return { ...out, ctr: ratio(out.clicks, out.impressions), cpc: ratio(out.cost, out.clicks),
    cpm: out.cost != null && out.impressions ? out.cost / out.impressions * 1000 : null,
    cpa: ratio(out.cost, out.conversions), roas: ratio(out.conversionsValue, out.cost),
    conversionRate: ratio(out.conversions, out.interactions), interactionRate: ratio(out.interactions, out.impressions),
    valuePerConversion: ratio(out.conversionsValue, out.conversions) };
}
export function overlayDays(campaigns, mixedCurrency, { partial = false } = {}) {
  const dates = new Map();
  const covered = campaigns.filter((c) => c.performanceV2?.daily?.length);
  for (const c of covered) for (const day of c.performanceV2.daily) dates.set(day.date, true);
  // Keep a stable reporting cohort for each metric throughout the plot. A
  // missing day within that cohort remains a gap, never a smaller account total.
  const cohorts = metricCohorts(covered);
  return [...dates.keys()].sort().map(date => {
    const coverage = {};
    const values = Object.fromEntries(V2_METRICS.map(({ key }) => {
      const inputs = metricInputs[key] || [key];
      const rows = cohorts[key].map(c => c.performanceV2.daily.find(d => d.date === date) || {});
      const available = rows.filter(row => inputs.every(field => row[field] != null && Number.isFinite(Number(row[field]))));
      coverage[key] = { reported: available.length, total: cohorts[key].length };
      return [key, aggregateMetrics(partial ? available : rows, mixedCurrency)[key]];
    }));
    return { date, ...values, ...(partial ? { coverage } : {}) };
  });
}
function metricCohorts(campaigns) {
  return Object.fromEntries(V2_METRICS.map(({ key }) => {
    const inputs = metricInputs[key] || [key];
    return [key, campaigns.filter((c) => c.performanceV2?.daily?.some((d) =>
      inputs.every((field) => d[field] != null && Number.isFinite(Number(d[field])))))];
  }));
}
export function groupAssessment(group, campaignMetrics) {
  const m = group.metrics || {};
  if ((!group.days && !group.cached) || m.cost == null || m.conversions == null) return 'No data for this range';
  if (!m.impressions && !m.cost) return 'No delivery';
  if (m.cost > 0 && m.conversions === 0) return 'Spend without conversions';
  if (m.conversions < 15 || m.clicks < 100) return 'Limited conversion data';
  if (m.cpa == null || campaignMetrics?.cpa == null) return 'No CPA comparison';
  return m.cpa <= campaignMetrics.cpa ? 'CPA at or below campaign' : 'CPA above campaign';
}

// Each tile states its own coverage. A platform that cannot report conversion
// value must not blank Google's value or silently contribute a zero to it.
const metricInputs = {
  ctr: ['clicks', 'impressions'], cpc: ['cost', 'clicks'], cpm: ['cost', 'impressions'],
  cpa: ['cost', 'conversions'], roas: ['conversionsValue', 'cost'],
  valuePerConversion: ['conversionsValue', 'conversions'],
  interactionRate: ['interactions', 'impressions'], conversionRate: ['conversions', 'interactions'],
};
export function metricCoverageTotals(campaigns, mixedCurrency) {
  const values = {}; const coverage = {};
  for (const { key } of V2_METRICS) {
    const inputs = metricInputs[key] || [key];
    const reported = campaigns.map((c) => c.performanceV2?.metrics || {}).filter((m) =>
      inputs.every((field) => m[field] != null && Number.isFinite(Number(m[field]))));
    values[key] = aggregateMetrics(reported, mixedCurrency)[key];
    coverage[key] = reported.length;
  }
  return { values, coverage };
}

// Use the same v2 daily evidence for the overlay, period chart and movers.
// Legacy endpoints may have different history/advertiser limits, so mixing
// those reads into one v2 dashboard can yield contradictory answers.
export function v2PeriodData(campaigns, compare = false) {
  const { mixed } = reportingCurrency(campaigns);
  const daily = overlayDays(campaigns, mixed);
  const complete = daily.slice(0, -1);
  const half = Math.floor(complete.length / 2);
  const enough = complete.length >= 4;
  const previous = enough ? complete.slice(complete.length - 2 * half, complete.length - half) : [];
  const current = enough ? complete.slice(complete.length - half) : [];
  const metadata = {
    latestDayExcluded: daily[daily.length - 1]?.date || null,
    campaignsCovered: campaigns.filter((c) => c.performanceV2?.days).length,
    newestPullAt: campaigns.map((c) => c.performanceV2?.fetchedAt).filter(Boolean).sort().pop() || null,
    daysAvailable: complete.length,
    daysPerPeriod: enough ? half : 0,
    compared: enough,
    currentRange: enough ? { start: current[0].date, end: current[current.length - 1].date } : null,
    priorRange: enough ? { start: previous[0].date, end: previous[previous.length - 1].date } : null,
    source: 'Performance v2 daily evidence',
  };
  const totalsFor = (campaign, days) => {
    const rows = days.map((d) => campaign.performanceV2?.daily?.find((r) => r.date === d.date));
    return { ...aggregateMetrics(rows.map((r) => r || {})), days: rows.filter(Boolean).length };
  };
  const cohorts = metricCohorts(campaigns);
  const periodTotals = (days) => Object.fromEntries(V2_METRICS.map(({ key }) => [key,
    aggregateMetrics(cohorts[key].map((c) => totalsFor(c, days)), mixed)[key]]));
  const deltas = { ...metadata, campaigns: enough ? Object.fromEntries(campaigns.map((c) => [c.id, {
    previous: totalsFor(c, previous), current: totalsFor(c, current),
  }])) : {}, reason: enough ? null : 'At least four complete reported days are needed to compare periods.' };
  const series = compare ? { ...metadata,
    totals: { current: periodTotals(current), previous: periodTotals(previous) },
    series: current.map((day, i) => ({ date: day.date, priorDate: previous[i].date, current: day, previous: previous[i] })),
    reason: deltas.reason,
  } : { ...metadata, compared: false, daysPerPeriod: complete.length, priorRange: null,
    totals: { current: periodTotals(complete), previous: null },
    currentRange: complete.length ? { start: complete[0].date, end: complete[complete.length - 1].date } : null,
    series: complete.map((day) => ({ date: day.date, current: day, previous: null })),
    reason: complete.length ? null : 'No complete daily data is available for this selection.',
  };
  return { series, deltas };
}


// Ranked campaign findings use each campaign's own saved days. Adding another
// campaign must not change this campaign's comparison window or measurements.
export function campaignPeriodDeltas(campaigns) {
  const entries = campaigns.map(campaign => {
    const { deltas } = v2PeriodData([campaign]);
    const values = deltas.campaigns[campaign.id];
    return [campaign.id, values ? { ...values, daysPerPeriod: deltas.daysPerPeriod,
      priorRange: deltas.priorRange, currentRange: deltas.currentRange } : null];
  }).filter(([, values]) => values);
  return { campaigns: Object.fromEntries(entries), independentPeriods: true,
    daysPerPeriod: Math.max(0, ...entries.map(([, values]) => values.daysPerPeriod)),
    comparedCampaigns: entries.length, totalCampaigns: campaigns.length,
    reason: 'Each campaign needs at least four reported days after excluding its latest reported day.',
  };
}
